'use client';
import * as React from 'react';
import Link from 'next/link';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import siteConfig from '@/config/site';
import Cart from '@/components/cart/Cart';

export interface NavItem {
  label: string;
  href: string;
}

/**
 * Site header. The template ships with an empty menu — every module is optional,
 * so you list the links this specific site needs via `nav` (and an optional
 * primary `cta`). Set `cart` to show the reservation cart trigger.
 */
export default function Header({
  nav = [],
  cta,
  cart = false,
}: {
  nav?: NavItem[];
  cta?: NavItem | null;
  cart?: boolean;
}) {
  const [open, setOpen] = React.useState(false);

  return (
    <AppBar position="sticky" sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ gap: 2 }}>
          <Typography
            component={Link}
            href="/"
            variant="h6"
            sx={{ fontWeight: 800, color: 'text.primary', textDecoration: 'none', flexGrow: 1 }}
          >
            {siteConfig.name}
          </Typography>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
            {nav.map((item) => (
              <Button key={item.href} component={Link} href={item.href} color="inherit">
                {item.label}
              </Button>
            ))}
            {cta && (
              <Button component={Link} href={cta.href} variant="contained" color="primary">
                {cta.label}
              </Button>
            )}
          </Box>

          {cart && <Cart />}

          {nav.length > 0 && (
            <IconButton
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
              onClick={() => setOpen(true)}
              aria-label="Ouvrir le menu"
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </Container>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 240 }} role="presentation" onClick={() => setOpen(false)}>
          <List>
            {[...nav, ...(cta ? [cta] : [])].map((item) => (
              <ListItemButton key={item.href} component={Link} href={item.href}>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
}
